import { useEffect } from 'react';

interface SeoMetaOptions {
  ogTitle?: string;
  ogDescription?: string;
  robots?: string;
}

function setOrCreateMeta(
  attribute: 'name' | 'property',
  value: string,
  content: string
): HTMLMetaElement | null {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${value}"]`);
  if (el) {
    el.setAttribute('content', content);
    return null; // pre-existing — don't track for removal
  }
  el = document.createElement('meta');
  el.setAttribute(attribute, value);
  el.setAttribute('content', content);
  document.head.appendChild(el);
  return el; // created — track for cleanup
}

export default function useSeoMeta(
  title: string,
  description: string,
  options?: SeoMetaOptions
): void {
  // Destructure to stable primitives so exhaustive-deps is satisfied
  const { robots, ogTitle, ogDescription } = options ?? {};

  // Effect 1: document title
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => {
      document.title = prev || 'Pinecurl';
    };
  }, [title]);

  // Effect 2: meta tags
  useEffect(() => {
    const created: HTMLMetaElement[] = [];

    const descEl = setOrCreateMeta('name', 'description', description);
    if (descEl) created.push(descEl);

    if (robots) {
      const robotsEl = setOrCreateMeta('name', 'robots', robots);
      if (robotsEl) created.push(robotsEl);
    }

    const resolvedOgTitle = ogTitle ?? title;
    const ogTitleEl = setOrCreateMeta('property', 'og:title', resolvedOgTitle);
    if (ogTitleEl) created.push(ogTitleEl);

    const resolvedOgDesc = ogDescription ?? description;
    const ogDescEl = setOrCreateMeta('property', 'og:description', resolvedOgDesc);
    if (ogDescEl) created.push(ogDescEl);

    return () => {
      created.forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, [title, description, robots, ogTitle, ogDescription]);
}
